/* eslint-disable no-console */
/* eslint-disable class-methods-use-this */
import { observable, computed, action } from 'mobx'
import { HUB_ROOT } from 'config/app.config'
import * as signalR from '@aspnet/signalr'

export default class Socket {
  static debugConnectionCounter = 0

  @observable connection
  @observable closed = 0
  @observable reconnecting = 0
  @observable mustReconnect = 0
  // 1 depois que start() terminou; o estado do objeto do SignalR não é observável pelo MobX,
  // então o isConnected (computed) precisa de um campo observável para recalcular
  @observable ready = 0

  @computed get isConnected() {
    // Só depois do start(): durante o handshake (Connecting) o invoke falha com
    // "Cannot send data if the connection is not in the 'Connected' State."
    return !!this.connection && this.ready === 1 && this.closed === 0
  }

  /** Estado real do SignalR agora (fora do MobX), usado antes de enviar. */
  canSend() {
    return (
      !!this.connection &&
      this.connection.state === signalR.HubConnectionState.Connected &&
      this.closed === 0
    )
  }

  @computed get isReconnecting() {
    return this.closed === 1 && this.reconnecting === 1
  }

  constructor(room, rootStore) {
    this.rootStore = rootStore
    this.room = room
    this.retryAttempt = 0
    this.hasConnectedOnce = false
    this.logTag = `[Socket:${room}]`
    this.connectPromise = null
    this.reconnectTimer = null

    setInterval(() => {
      console.debug(this.logTag, 'health-check tick', {
        hasConnectionRef: !!this.connection,
        state: this.connection?.state,
        isConnected: this.isConnected,
        isReconnecting: this.isReconnecting,
        mustReconnect: this.mustReconnect,
      })
      // Deveria estar conectado, mas a conexão caiu sem disparar onclose e nada está tentando de novo
      const dead =
        !this.connection || this.connection.state === signalR.HubConnectionState.Disconnected
      if (this.mustReconnect === 1 && dead && !this.connectPromise && !this.reconnectTimer) {
        console.warn(this.logTag, 'health-check detected dead connection, forcing reconnect')
        this.reconnect()
      }
    }, 10000)
  }

  async connectAsync() {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_ROOT}/${this.room}`, {
        accessTokenFactory: () => this.rootStore.authStore.accessToken,
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()
    this.connection.serverTimeoutInMilliseconds = 120000
    this.configureHub()

    const current = this.connection
    try {
      await current.start()
      current.onclose(async (error) => {
        if (error && this.connection === current) await this.reconnect()
      })

      this.ready = 1
      if (this.connectedHandler) this.connectedHandler()
      this.closed = 0
      this.reconnecting = 0
      this.mustReconnect = 1
    } catch (e) {
      throw Error()
    }
  }

  /**
   * Abre a conexão. Chamadas simultâneas reaproveitam a mesma tentativa, para não abrir
   * várias conexões em paralelo (cada uma cancelava o handshake da anterior).
   */
  @action.bound connect() {
    if (this.connectPromise) return this.connectPromise
    const run = async () => {
      try {
        await this.openConnection()
      } finally {
        this.connectPromise = null
      }
    }
    this.connectPromise = run()
    return this.connectPromise
  }

  async openConnection() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    // a partir daqui a tela quer ficar conectada; disconnect() volta para 0
    this.mustReconnect = 1

    const token = this.rootStore.authStore.accessToken
    console.debug(this.logTag, 'connect() called', {
      hasToken: !!token,
      tokenExpired: this.rootStore.authStore.accessTokenExpired,
      previousConnection: this.connection ? this.connection.connectionId : null,
    })

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_ROOT}/${this.room}`, {
        accessTokenFactory: () => this.rootStore.authStore.accessToken,
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()
    newConnection.serverTimeoutInMilliseconds = 120000
    newConnection.__debugId = ++Socket.debugConnectionCounter
    this.ready = 0
    this.connection = newConnection
    this.configureHub()

    try {
      await newConnection.start()
      if (this.connection !== newConnection || this.mustReconnect !== 1) {
        // a tela saiu (disconnect) ou outra conexão assumiu durante o handshake
        console.info(this.logTag, 'connection started but is no longer wanted, stopping', {
          debugId: newConnection.__debugId,
        })
        newConnection.stop().catch(() => {})
        return
      }
      console.info(this.logTag, 'connection started', {
        debugId: newConnection.__debugId,
        connectionId: newConnection.connectionId,
        state: newConnection.state,
      })

      newConnection.onclose(async (error) => {
        const isCurrent = this.connection === newConnection
        if (isCurrent) this.ready = 0
        console.info(this.logTag, 'onclose fired', {
          debugId: newConnection.__debugId,
          error,
          isStillCurrentConnection: isCurrent,
          mustReconnect: this.mustReconnect,
        })
        // conexões antigas (já substituídas) não disparam reconexão
        if (isCurrent && this.mustReconnect === 1) this.reconnect()
      })

      const wasReconnect = this.hasConnectedOnce
      this.hasConnectedOnce = true
      this.retryAttempt = 0
      this.ready = 1
      this.closed = 0
      this.reconnecting = 0

      console.debug(this.logTag, 'invoking connectedHandler', { wasReconnect })
      if (this.connectedHandler) {
        // não espera o handler: ele pode chamar connect()/invoke, que esperariam esta mesma promessa
        try {
          const result = this.connectedHandler(wasReconnect)
          if (result && typeof result.catch === 'function') {
            result.catch((connectedHandlerError) =>
              console.error(this.logTag, "Error on 'onConnected' handler", connectedHandlerError)
            )
          }
        } catch (connectedHandlerError) {
          console.error(this.logTag, "Error on 'onConnected' handler", connectedHandlerError)
        }
      }
    } catch (e) {
      if (this.connection !== newConnection || this.mustReconnect !== 1) {
        // handshake cancelado porque a própria tela parou ou trocou a conexão: não é erro
        console.info(this.logTag, 'connect() aborted on purpose', { debugId: newConnection.__debugId })
        return
      }
      console.error(this.logTag, 'connect() failed', {
        debugId: newConnection.__debugId,
        error: e,
        statusCode: e && e.statusCode,
      })
      if (e && e.statusCode === 401) {
        try {
          await this.rootStore.authStore.refreshAccessToken()
        } catch (refreshError) {
          console.error(this.logTag, 'refresh token failed', refreshError)
        }
      }
      this.reconnect()
    }
  }

  /** Agenda uma nova tentativa (uma por vez), esperando mais a cada falha: 2 s, 4 s, 8 s… até 30 s. */
  @action.bound reconnect() {
    if (this.reconnectTimer) return
    this.closed = 1
    this.reconnecting = 1
    this.retryAttempt += 1
    const delay =
      Math.min(30000, 2000 * 2 ** Math.min(this.retryAttempt - 1, 4)) + Math.round(Math.random() * 1000)
    console.warn(this.logTag, 'reconnect() scheduled', {
      attempt: this.retryAttempt,
      delay,
      currentConnectionId: this.connection ? this.connection.__debugId : null,
    })

    if (this.connectionRetryHandler) {
      try {
        this.connectionRetryHandler(this.retryAttempt)
      } catch (connectionRetryHandlerError) {
        console.error("Error on 'onConnectionRetry' handler", connectionRetryHandlerError)
      }
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.mustReconnect !== 1) return
      console.debug(this.logTag, 'reconnect() timer firing, calling connect()')
      this.connect()
    }, delay)
  }

  /**
   * invoke que espera a conexão ficar pronta (até 10 s) em vez de falhar durante o handshake
   * ou numa reconexão.
   */
  async invoke(method, ...args) {
    if (!this.canSend()) {
      const dead =
        !this.connection || this.connection.state === signalR.HubConnectionState.Disconnected
      if (this.connectPromise) await this.connectPromise
      else if (dead && !this.reconnectTimer) await this.connect()
      const until = Date.now() + 10000
      while (!this.canSend() && Date.now() < until) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 250))
      }
    }
    if (!this.canSend()) {
      throw new Error('Sem conexão com o servidor em tempo real. Tente novamente em alguns segundos.')
    }
    return this.connection.invoke(method, ...args)
  }

  @action async disconnect() {
    this.mustReconnect = 0
    this.ready = 0
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    const connectionToStop = this.connection
    console.debug(this.logTag, 'disconnect() called', {
      debugId: connectionToStop ? connectionToStop.__debugId : null,
    })
    if (!connectionToStop) return

    try {
      await connectionToStop.stop()
    } catch (error) {
      console.error(this.logTag, 'disconnect() error', error)
    } finally {
      // Only clear the reference if it's still the connection we stopped.
      // If connect() already replaced it (e.g. fast navigation between
      // screens that share this hub instance), clearing it here would
      // orphan the new, live connection and leave the UI stuck showing
      // "disconnected" with no automatic recovery.
      if (this.connection === connectionToStop) {
        console.debug(this.logTag, 'disconnect() clearing connection reference', {
          debugId: connectionToStop.__debugId,
        })
        this.connection = null
      } else {
        console.warn(
          this.logTag,
          'disconnect() skipped clearing connection: a newer connection is now active',
          {
            stoppedDebugId: connectionToStop.__debugId,
            currentDebugId: this.connection ? this.connection.__debugId : null,
          }
        )
      }
    }
  }

  configureHub() {}

  connectionRetryHandler
  onConnectionRetry = (handler) => (this.connectionRetryHandler = handler)
  connectedHandler
  onConnected = (handler) => (this.connectedHandler = handler)
  errorHandler
  onError = (handler) => (this.errorHandler = handler)
}
